/**
 * Altitude profile display for selected transits in the review dialog.
 * Shows a table of altitude at key time points for each selected transit.
 */
import React from 'react';
import {
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { formatTime } from '../utils/timeFormatting';
import { calculateTargetAltitudes } from '../utils/observabilityCalculations';

const AltitudeRow = ({ label, point, minAltitude, extra }) => (
  <TableRow sx={extra?.sx}>
    <TableCell sx={{ py: 0.5 }}>
      {extra?.bold ? <strong>{label}</strong> : label}
      {point.adjusted && (
        <Chip label="Adjusted" color="info" size="small" sx={{ ml: 1 }} />
      )}
    </TableCell>
    <TableCell sx={{ py: 0.5, ...(extra?.cellSx || {}) }}>
      {extra?.bold ? <strong>{formatTime(point.time)}</strong> : formatTime(point.time)}
    </TableCell>
    <TableCell align="right" sx={{ py: 0.5, ...(extra?.cellSx || {}) }}>
      {extra?.bold ? (
        <strong>{point.altitude !== null ? `${point.altitude.toFixed(1)}` : 'N/A'}</strong>
      ) : (
        point.altitude !== null ? `${point.altitude.toFixed(1)}` : 'N/A'
      )}
    </TableCell>
    <TableCell sx={{ py: 0.5 }}>
      {point.altitude !== null && point.altitude >= minAltitude
        ? <Chip label="Observable" color="success" size="small" />
        : <Chip label="Below Limit" color="error" size="small" />
      }
    </TableCell>
  </TableRow>
);

const TransitAltitudeProfile = ({ transit, observatory, baselineBefore, baselineAfter, minAltitude }) => {
  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);
  const observationStartTime = new Date(ingressTime.getTime() - (baselineBefore * 60 * 1000));
  const observationEndTime = new Date(egressTime.getTime() + (baselineAfter * 60 * 1000));

  const altitudes = calculateTargetAltitudes(
    transit, observatory, observationStartTime, observationEndTime, minAltitude
  );

  if (!altitudes) return null;

  const anyBelowMin = [
    altitudes.observationStart,
    altitudes.ingress,
    altitudes.midpoint,
    altitudes.egress,
    altitudes.observationEnd
  ].some(point => point.altitude !== null && point.altitude < minAltitude);

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
        {transit.exoplanet?.name || 'Unknown Target'}
      </Typography>

      {anyBelowMin && (
        <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
          Target will be below {minAltitude} altitude during parts of this observation.
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5 }}><strong>Time Point</strong></TableCell>
              <TableCell sx={{ py: 0.5 }}><strong>UTC Time</strong></TableCell>
              <TableCell align="right" sx={{ py: 0.5 }}><strong>Altitude</strong></TableCell>
              <TableCell sx={{ py: 0.5 }}><strong>Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AltitudeRow label="Baseline Start" point={altitudes.observationStart} minAltitude={minAltitude} />
            <AltitudeRow label="Ingress" point={altitudes.ingress} minAltitude={minAltitude} />
            <AltitudeRow
              label="Midpoint"
              point={altitudes.midpoint}
              minAltitude={minAltitude}
              extra={{
                bold: true,
                sx: { bgcolor: 'primary.main', '& td': { color: 'primary.contrastText' } },
                cellSx: { color: 'primary.contrastText' }
              }}
            />
            <AltitudeRow label="Egress" point={altitudes.egress} minAltitude={minAltitude} />
            <AltitudeRow label="Baseline End" point={altitudes.observationEnd} minAltitude={minAltitude} />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const AltitudeProfileSection = ({
  selectedObservatoryId,
  selectedTransits,
  transits,
  observatories,
  baselineBefore,
  baselineAfter,
  minAltitude,
}) => {
  if (!selectedObservatoryId || selectedTransits.size === 0) return null;

  const selectedObservatory = observatories.find(o => o._id === selectedObservatoryId);
  if (!selectedObservatory) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
        Target Altitude Profiles ({selectedTransits.size} selected)
      </Typography>
      {Array.from(selectedTransits).map(transitId => {
        const transit = transits.find(t => {
          const tId = `${t.exoplanet.name}_${new Date(t.transit.time_utc).getTime()}`;
          return tId === transitId;
        });
        if (!transit) return null;

        return (
          <TransitAltitudeProfile
            key={`altitude-preview-${transitId}`}
            transit={transit}
            observatory={selectedObservatory}
            baselineBefore={baselineBefore}
            baselineAfter={baselineAfter}
            minAltitude={minAltitude}
          />
        );
      })}
    </Box>
  );
};

export default AltitudeProfileSection;
